import {
  CATALOGUE,
  CURRENCIES,
  DEFAULT_CURRENCY,
  FOUNDING,
  TAX_INCLUSIVE,
  annualSaving,
  formatAmount,
  foundingAmount,
  perMonth,
  priceOf,
  type Currency,
  type Interval,
} from '../../shared/billing/catalogue';
import { OFFERS } from '../../shared/billing/offer';
import type { ListedPrice } from './json-ld';
import {
  PLANS,
  RULE_LIMIT,
  can,
  type Feature,
  type PaidPlan,
  type Plan,
} from '../../shared/billing/plan';
import type { Faq } from './data';

/**
 * The price list as the public pages print it: /pricing's three columns, its
 * comparison table and billing answers, and the landing page's teaser. Every
 * amount comes from the catalogue and every plan's contents from OFFERS
 * (src/shared/billing/), so these pages, the dashboard's Settings and
 * checkout cannot quote different things.
 *
 * Pure, like the rest of this folder: the controller builds the page from it,
 * and the browser runs the same functions when it moves the page to the
 * visitor's own currency or the visitor switches billing, so the two never
 * disagree about a price.
 */

export const BILLINGS = ['annual', 'monthly'] as const;
export type Billing = (typeof BILLINGS)[number];

export const BILLING_LABELS: Record<Billing, string> = {
  annual: 'Annual',
  monthly: 'Monthly',
};

const INTERVAL: Record<Billing, Interval> = {
  annual: 'year',
  monthly: 'month',
};

/** The word after "billed" on a card and in an Offer's name: "billed yearly". */
const BILLED: Record<Billing, string> = {
  annual: 'yearly',
  monthly: 'monthly',
};

/** What the page shows: one of the five price lists, annual or monthly. */
export interface Selection {
  currency: Currency;
  billing: Billing;
}

/**
 * The page as the server draws it. It is one copy for everyone, which a CDN
 * can keep, so the server never chooses a currency by the visitor's address:
 * it draws dollars, the largest market's, for crawlers and readers without
 * JavaScript, and the script then moves it to the currency for where the
 * visitor is (client/pages.ts). Annual is the default billing, shown as what
 * it costs a month.
 */
export const DEFAULT_SELECTION: Selection = {
  currency: 'usd',
  billing: 'annual',
};

function oneOf<T extends string>(
  allowed: readonly T[],
  value: unknown,
): T | null {
  const wanted = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return allowed.find((a) => a === wanted) ?? null;
}

/** `?billing=` as one of ours, in either case; null for anything else. */
export function billingIn(value: unknown): Billing | null {
  return oneOf(BILLINGS, value);
}

/**
 * The selection a query string asks for, which is only ever the billing:
 * the currency goes by where the visitor is, so a shared link shows each
 * reader their own prices, and a `?currency=` from an older link is ignored.
 * A missing or unknown billing falls back to the default, so every address
 * draws a page.
 */
export function selectionFrom(query: { billing?: unknown }): Selection {
  return {
    currency: DEFAULT_SELECTION.currency,
    billing: billingIn(query.billing) ?? DEFAULT_SELECTION.billing,
  };
}

/** The page for one billing. */
export function pricingHref(billing: Billing): string {
  return `/pricing?billing=${billing}`;
}

/**
 * Where a paid plan's button goes: the dashboard's Settings, which starts
 * checkout for that plan and interval. Signed out, the dashboard asks for a
 * sign-in first and carries on from there. `appUrl` is the address the
 * header's "Sign In" uses, and ends in a slash.
 */
export function checkoutHref(
  appUrl: string,
  plan: PaidPlan,
  billing: Billing,
): string {
  return `${appUrl}settings?plan=${planId(plan)}&interval=${INTERVAL[billing]}`;
}

/** Where the Free plan's button goes on /pricing: the home page's signup. */
const SIGNUP_HREF = '/';

/** Both paid plans' button; `trialDays` is TRIAL_DAYS. */
export function trialCta(trialDays: number): string {
  return `Start ${trialDays}-day free trial`;
}

/** "alerts" for ALERTS: a plan in an address, an element id, a data attribute. */
export function planId(plan: Plan): string {
  return plan.toLowerCase();
}

/** The currencies in a sentence. */
const CURRENCY_NAMES: Record<Currency, string> = {
  eur: 'euros',
  usd: 'US dollars',
  gbp: 'pounds',
  cad: 'Canadian dollars',
  aud: 'Australian dollars',
};

/** "a, b and c". */
function list(items: string[]): string {
  return items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Whether tax is already in a price, as a card's small print says it. */
function taxNote(currency: Currency): string {
  return TAX_INCLUSIVE[currency]
    ? 'Includes tax'
    : 'Plus tax at checkout, where it applies';
}

/** What a plan costs on one selection, as the lines a card prints. */
export interface PriceLines {
  /** "$10/mo": a month on this billing, an annual price over twelve. */
  price: string;
  /** "$120 billed yearly", "billed monthly"; "forever" on Free. */
  billed: string;
  /** What annual billing saves on this plan; null on Free. */
  saving: string | null;
  /** Null on Free, which has nothing to tax. */
  tax: string | null;
  /** The founding price, while the offer runs and annual is chosen. */
  founding: string | null;
}

/**
 * An annual price is printed as what it comes to a month, which is rounded
 * to the cent, so the yearly total is always stated beside it: that is the
 * amount charged. `founding` is the server's word that the coupon is on.
 */
export function priceLines(
  plan: Plan,
  { currency, billing }: Selection,
  founding: boolean,
): PriceLines {
  if (plan === 'FREE') {
    return {
      price: formatAmount(0, currency),
      billed: 'forever',
      saving: null,
      tax: null,
      founding: null,
    };
  }
  const yearly = billing === 'annual';
  const annual = priceOf(plan, 'year').amounts[currency];
  const month = perMonth(priceOf(plan, INTERVAL[billing]), currency);
  const saving = annualSaving(plan, currency);
  return {
    price: `${formatAmount(month, currency)}/mo`,
    billed: yearly
      ? `${formatAmount(annual, currency)} billed ${BILLED.annual}`
      : `billed ${BILLED.monthly}`,
    saving: yearly
      ? `${saving}% less than paying monthly`
      : `${saving}% less if you pay yearly`,
    tax: taxNote(currency),
    founding:
      founding && yearly
        ? `Founding members pay ${formatAmount(foundingAmount(annual), currency)} a year`
        : null,
  };
}

/**
 * Every price /pricing lists in one currency, for its SoftwareApplication
 * (site/json-ld.ts): Free once, at nothing, as its card says "forever" on
 * either billing, then each paid plan on each billing, in the page's order.
 * The amounts are the catalogue's, which the cards print (priceLines): a
 * monthly price as the "/mo" figure, an annual one as the yearly total
 * billed. The founding coupon is not a price of its own.
 */
export function listedPrices(currency: Currency): ListedPrice[] {
  return PLANS.flatMap((plan): ListedPrice[] =>
    plan === 'FREE'
      ? [
          {
            name: OFFERS[plan].name,
            amount: 0,
            currency,
            every: null,
            taxIncluded: null,
          },
        ]
      : BILLINGS.map((billing) => ({
          name: `${OFFERS[plan].name}, billed ${BILLED[billing]}`,
          amount: priceOf(plan, INTERVAL[billing]).amounts[currency],
          currency,
          every: INTERVAL[billing],
          taxIncluded: TAX_INCLUSIVE[currency],
        })),
  );
}

/** One column of /pricing, and one card of the landing page's teaser. */
export interface PlanCard extends PriceLines {
  /** planId(): "free", "alerts", "pro". */
  id: string;
  name: string;
  line: string;
  features: string[];
  featured: boolean;
  cta: string;
  href: string;
  /** The button goes into the dashboard (its checkout), so it is linked nofollow. */
  app: boolean;
}

/** The three columns, Free first. What each holds is OFFERS, word for word. */
export function planCards(
  selection: Selection,
  options: { appUrl: string; founding: boolean; trialDays: number },
): PlanCard[] {
  return PLANS.map((plan) => {
    const { name, line, features } = OFFERS[plan];
    return {
      id: planId(plan),
      name,
      line,
      features,
      // Alerts is the plan the product is named for. It carries no "most
      // chosen" badge: that waits until it is true.
      featured: plan === 'ALERTS',
      ...priceLines(plan, selection, options.founding),
      ...(plan === 'FREE'
        ? { cta: 'Get the Weekly Sonar', href: SIGNUP_HREF, app: false }
        : {
            cta: trialCta(options.trialDays),
            href: checkoutHref(options.appUrl, plan, selection.billing),
            app: true,
          }),
    };
  });
}

/**
 * The line over the plans while the founding offer runs. The figures are the
 * coupon's own (FOUNDING), so the page cannot promise a different discount
 * from the one checkout applies.
 */
export const FOUNDING_BANNER = `Founding-member offer · ${FOUNDING.percentOff}% off annual plans for as long as your subscription stays active · first ${FOUNDING.maxRedemptions} subscribers`;

export function pricingLede(trialDays: number): string {
  return `${OFFERS.ALERTS.name} and ${OFFERS.PRO.name} start with a ${trialDays}-day free trial. You need a card to start, and you are not charged if you cancel before it ends.`;
}

/** Under the plans: what happens to everyone the five lists leave out. */
export const CONVERSION_NOTE = `Paying in another currency? You are charged the price in ${CURRENCY_NAMES[DEFAULT_CURRENCY]}, converted to your currency by Stripe at checkout.`;

/**
 * What the script shows a visitor it took for somewhere outside the five
 * currencies' own countries, over the euro list it switched them to.
 */
export const CONVERTED_NOTE = `These are the prices in ${CURRENCY_NAMES[DEFAULT_CURRENCY]}. Checkout converts them to your local currency.`;

/** The page's meta description, for search results and link previews. */
export function pricingDescription(trialDays: number): string {
  return `Raise Sonar plans: ${list(PLANS.map((p) => OFFERS[p].name))}. ${OFFERS.ALERTS.name} and ${OFFERS.PRO.name} start with a ${trialDays}-day free trial. Prices in ${list(CURRENCIES.map((c) => c.toUpperCase()))}.`;
}

// ---------------------------------------------------------------------------
// Compare plans
// ---------------------------------------------------------------------------

/** true is a tick, false a dash, and a string is printed as it is. */
type Cell = boolean | string;

export interface MatrixRow {
  label: string;
  /** The gate the row reads, so a test can see every gate has a row. */
  gate: Feature | null;
  /** One per plan, in PLANS' order. */
  cells: Cell[];
}

interface Row {
  label: string;
  gate?: Feature;
  /** What the cell says; left out, a tick where the gate is open. */
  cell?: (plan: Plan) => Cell;
}

/**
 * The comparison table. Every cell is read off the gates the API enforces
 * (plan.ts), never typed as a tick, so the table cannot say a plan has
 * something the dashboard then refuses. The rows without a gate are on
 * every plan.
 */
const ROWS: Row[] = [
  { label: 'Weekly Sonar email' },
  {
    label: 'Feed',
    gate: 'LIVE_FEED',
    cell: (p) => (can(p, 'LIVE_FEED') ? 'Live' : '24–48 hours behind'),
  },
  {
    label: 'Filters',
    gate: 'ALL_FILTERS',
    cell: (p) => (can(p, 'ALL_FILTERS') ? 'Every filter' : 'Region and status'),
  },
  { label: 'Eligibility for your country on every raise' },
  { label: 'Launch alerts by email', gate: 'ALERTS' },
  {
    label: 'Closing reminders at 7 days, 72 hours and 24 hours',
    gate: 'ALERTS',
  },
  { label: 'Near-cap, perk-expiry and term-change alerts', gate: 'ALERTS' },
  { label: 'Momentum alarms on raises you follow', gate: 'ALERTS' },
  {
    label: 'Alert rules',
    gate: 'ALERTS',
    cell: (p) => (RULE_LIMIT[p] > 0 ? `Up to ${RULE_LIMIT[p]}` : false),
  },
  { label: 'Watchlist and portfolio', gate: 'WATCHLIST' },
  { label: 'Calendar with ICS export', gate: 'CALENDAR' },
  { label: 'Stated financials', gate: 'FINANCIALS' },
  {
    label: 'Momentum history, similar-raise comparisons and sort by pace',
    gate: 'MOMENTUM',
  },
  { label: 'Excel exports and watchlist CSV', gate: 'EXPORT' },
];

export function matrix(): MatrixRow[] {
  return ROWS.map(({ label, gate, cell }) => ({
    label,
    gate: gate ?? null,
    cells: PLANS.map((plan) =>
      cell ? cell(plan) : gate ? can(plan, gate) : true,
    ),
  }));
}

// ---------------------------------------------------------------------------
// Billing questions
// ---------------------------------------------------------------------------

/** The smallest and largest annual saving on the list: "31–34%", or "33%". */
function savingRange(): string {
  const all = CATALOGUE.filter((p) => p.interval === 'year').flatMap((p) =>
    CURRENCIES.map((c) => annualSaving(p.plan, c)),
  );
  const [low, high] = [Math.min(...all), Math.max(...all)];
  return low === high ? `${low}%` : `${low}–${high}%`;
}

const named = (inclusive: boolean) =>
  list(
    CURRENCIES.filter((c) => TAX_INCLUSIVE[c] === inclusive).map(
      (c) => CURRENCY_NAMES[c],
    ),
  );

/** On /pricing and among the landing page's questions, in the same words. */
export const CANCEL_FAQ: Faq = {
  q: 'How do I change plan, update my card or cancel?',
  a: 'From Settings in the dashboard, which opens Stripe’s billing portal, or at link.com. A cancelled plan runs to the end of the period you have paid for, and you keep the free Weekly Sonar.',
};

/**
 * /pricing's answers. The founding offer is only explained while it runs:
 * `founding` is the server's word that the coupon is on. `trialDays` is
 * TRIAL_DAYS.
 */
export function billingFaqs(founding: boolean, trialDays: number): Faq[] {
  return [
    {
      q: 'How does the free trial work?',
      a: `Alerts and Pro start with ${trialDays} days free. You enter a card to start, you are reminded by email before the trial ends, and you are not charged if you cancel before then. A second subscription does not get a second trial.`,
    },
    {
      q: 'Which currency am I charged in?',
      a: `The one for the country you are browsing from: checkout goes by where you are, and this page shows you the prices it will charge. Prices are set in ${list(CURRENCIES.map((c) => CURRENCY_NAMES[c]))}. Prices in ${named(true)} include tax. In ${named(false)}, tax is added at checkout where it applies. Anywhere else you see the prices in ${CURRENCY_NAMES[DEFAULT_CURRENCY]}, and Stripe converts them to your currency at checkout.`,
    },
    {
      q: 'How much less does annual billing cost?',
      a: `About a third less than twelve months paid monthly: ${savingRange()}, depending on the plan and the currency. Annual prices are shown as what they come to a month, with the yearly total beside them.`,
    },
    ...(founding
      ? [
          {
            q: 'What is the founding-member offer?',
            a: `${FOUNDING.percentOff}% off annual plans for as long as your subscription stays active, for the first ${FOUNDING.maxRedemptions} subscribers. The discount is applied at checkout when you choose annual billing.`,
          },
        ]
      : []),
    {
      q: 'Who takes the payment, and what will my statement say?',
      a: 'Stripe takes the payment as merchant of record, under the name Link. Your receipts come from Link, and your card statement shows LINK.COM*.',
    },
    CANCEL_FAQ,
    {
      q: 'Is there a plan for teams, or an API?',
      a: 'Not at checkout. If you need the data for a team or a tool, tell us what you need on the Data & API page.',
    },
  ];
}
